import { useCallback, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';
type Density = 'comfortable' | 'compact';

/** Theme + density controls that stamp <html> data attributes (see index.html
 * pre-paint script + tokens.css). Persisted to localStorage. */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem('bc-theme') as Theme) ?? 'system',
  );
  const [density, setDensityState] = useState<Density>(
    () => (localStorage.getItem('bc-density') as Density) ?? 'comfortable',
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
      localStorage.removeItem('bc-theme');
    } else {
      root.setAttribute('data-theme', theme);
      localStorage.setItem('bc-theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (density === 'comfortable') {
      root.removeAttribute('data-density');
      localStorage.removeItem('bc-density');
    } else {
      root.setAttribute('data-density', density);
      localStorage.setItem('bc-density', density);
    }
  }, [density]);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((d) => (d === 'compact' ? 'comfortable' : 'compact'));
  }, []);

  return { theme, density, setTheme: setThemeState, setDensity: setDensityState, toggleTheme, toggleDensity };
}
